import { useCallback, useRef, useState } from 'react'

export type OrchestratorPhase =
  | 'idle'
  | 'prefetching'
  | 'decomposing'
  | 'executing'
  | 'verifying'
  | 'synthesizing'
  | 'done'
  | 'error'

export interface OrchTask {
  id: string
  agent_name: string
  description?: string
  status: 'pending' | 'running' | 'completed' | 'failed'
}

export interface OrchestratorState {
  phase: OrchestratorPhase
  requestId: string
  prefetch?: { entities?: number; confidence?: number }
  tasks: OrchTask[]
  groupsTotal: number
  groupsDone: number
  verification?: { verdict?: string; checks?: number }
  output?: string
  errorMessage?: string
  startedAt?: number
  endedAt?: number
}

const INITIAL: OrchestratorState = {
  phase: 'idle',
  requestId: '',
  tasks: [],
  groupsTotal: 0,
  groupsDone: 0,
}

/**
 * Hook that streams auto-orchestrator SSE events from
 * POST /api/v1/agents/auto-orchestrator/invoke/stream and folds them into
 * a single reactive state the UI can render.
 */
export function useOrchestratorStream() {
  const [state, setState] = useState<OrchestratorState>(INITIAL)
  const abortRef = useRef<AbortController | null>(null)

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setState(INITIAL)
  }, [])

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setState((s) => (s.phase === 'idle' ? s : { ...s, phase: 'idle' }))
  }, [])

  const run = useCallback(async (prompt: string, context: Record<string, any> = {}, agentName = 'auto-orchestrator') => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setState({ ...INITIAL, phase: 'prefetching', startedAt: Date.now() })

    const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '')
    const url = `${baseUrl}/api/v1/agents/${agentName}/invoke/stream`
    const token = localStorage.getItem('access_token')

    let resp: Response
    try {
      resp = await fetch(url, {
        method: 'POST',
        signal: ctrl.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ prompt, context }),
      })
    } catch (err: any) {
      if (ctrl.signal.aborted) return
      setState((s) => ({ ...s, phase: 'error', errorMessage: err?.message || 'Network error', endedAt: Date.now() }))
      return
    }

    if (!resp.ok || !resp.body) {
      setState((s) => ({
        ...s,
        phase: 'error',
        errorMessage: `HTTP ${resp.status}`,
        endedAt: Date.now(),
      }))
      return
    }

    const reader = resp.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''

    const apply = (evt: string, payload: any) => {
      setState((s) => {
        switch (evt) {
          case 'orchestration.context_fetching':
            return { ...s, phase: 'prefetching' }
          case 'orchestration.context_ready':
            return {
              ...s,
              phase: 'decomposing',
              prefetch: {
                entities: payload.entity_count ?? payload.entities,
                confidence: payload.confidence,
              },
            }
          case 'orchestration.decomposed': {
            const tasks: OrchTask[] = (payload.tasks || []).map((t: any) => ({
              id: t.id,
              agent_name: t.agent_name,
              description: t.description || t.prompt,
              status: 'pending' as const,
            }))
            return {
              ...s,
              phase: 'executing',
              tasks,
              groupsTotal: payload.group_count || 0,
              requestId: payload.request_id || s.requestId,
            }
          }
          case 'orchestration.group_complete': {
            const completed = new Set<string>(payload.completed || [])
            const failed = new Set<string>(payload.failed || [])
            return {
              ...s,
              groupsDone: payload.group || s.groupsDone,
              tasks: s.tasks.map((t) =>
                completed.has(t.id)
                  ? { ...t, status: 'completed' }
                  : failed.has(t.id)
                    ? { ...t, status: 'failed' }
                    : t,
              ),
            }
          }
          case 'verification':
            return {
              ...s,
              phase: 'verifying',
              verification: { verdict: payload.verdict, checks: payload.checks },
            }
          case 'phase':
            if (payload.phase === 'verifying') return { ...s, phase: 'verifying' }
            if (payload.phase === 'synthesizing') return { ...s, phase: 'synthesizing' }
            return s
          case 'result':
            return {
              ...s,
              phase: 'done',
              output: payload.output || '',
              requestId: payload.request_id || s.requestId,
              endedAt: Date.now(),
            }
          case 'error':
            return {
              ...s,
              phase: 'error',
              errorMessage: payload.error || 'Orchestrator error',
              endedAt: Date.now(),
            }
          default:
            return s
        }
      })
    }

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })

        // SSE frames are delimited by a blank line.
        let sep = buf.indexOf('\n\n')
        while (sep !== -1) {
          const frame = buf.slice(0, sep)
          buf = buf.slice(sep + 2)
          let evt = 'message'
          let data = ''
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) evt = line.slice(6).trim()
            else if (line.startsWith('data:')) data += line.slice(5).trim()
          }
          if (data) {
            try {
              apply(evt, JSON.parse(data))
            } catch {
              // keepalive / non-JSON frames are ignored
            }
          }
          sep = buf.indexOf('\n\n')
        }
      }
    } catch (err: any) {
      if (ctrl.signal.aborted) return
      setState((s) => ({ ...s, phase: 'error', errorMessage: err?.message || 'Stream interrupted', endedAt: Date.now() }))
    }
  }, [])

  return { state, run, cancel, reset }
}
